// physicsGCSE.js — AQA-aligned KS4 Physics (Y10/Y11). Sections P1-P7.
// Compact factory matching biology/chemistry GCSE pattern.

const gt = (id, slug, title, c) => ({
  id, slug, title,
  difficulty: c.tier || 'foundation',
  pageRef: c.pageRef || '',
  ...(c.diagram ? { diagram: c.diagram } : {}),
  learn: {
    ...(c.intro ? { explanation: c.intro } : {}),
    ...(c.chunks ? { chunks: c.chunks } : {}),
    keyPoints: c.keyPoints || [],
    ...(c.we ? { workedExamples: Array.isArray(c.we) ? c.we : [c.we] } : {}),
    ...(c.whyItWorks ? { whyItWorks: c.whyItWorks } : {}),
  },
  glossary: c.glossary || [],
  ...(c.rp ? { requiredPractical: c.rp } : {}),
  ...(c.examQ ? { examQuestion: c.examQ } : {}),
  ...(c.examinerInsights ? { examinerInsights: c.examinerInsights } : {}),
  practice: {
    warmUp: c.practice?.warmUp || [],
    level1: c.practice?.level1 || [],
    level2: c.practice?.level2 || [],
    level3: c.practice?.level3 || [],
  },
});

const tQ = (id, q, a, h1, h2, h3) => ({ id, type: 'text', question: q, answer: a, hint1: h1, hint2: h2, hint3: h3 });
const nQ = (id, q, a, h1, h2, h3) => ({ id, type: 'number', question: q, answer: a, hint1: h1, hint2: h2, hint3: h3 });
const cQ = (id, q, opts, a, h1, h2, h3) => ({ id, type: 'choice', question: q, options: opts, answer: a, hint1: h1, hint2: h2, hint3: h3 });

// =============================================================
// P1 — Energy
// =============================================================
export const p1EnergyTopics = [
  gt('gcse-p1-1', 'energy-stores-transfers', 'Energy Stores & Transfers', {
    pageRef: 'P1.1',
    intro: 'Energy is stored in different ways: kinetic (moving objects), gravitational potential (raised objects), elastic potential (stretched/compressed), thermal (warm objects), chemical (food, fuel, batteries), magnetic, electrostatic, nuclear. Energy is TRANSFERRED between stores by 4 mechanisms: mechanically (force × distance — work done), electrically (current carrying charge), by heating, by radiation (e.g. light, sound). Energy can NEVER be created or destroyed — only transferred.',
    keyPoints: [
      'Energy stores: kinetic, GPE, elastic, thermal, chemical, magnetic, electrostatic, nuclear',
      'Transfer mechanisms: mechanically, electrically, by heating, by radiation',
      'Conservation: total energy in a closed system stays constant',
      'Useful energy + wasted energy (often as heat to surroundings)',
    ],
    glossary: [
      { term: 'Energy store', definition: 'A way energy can be stored (e.g. kinetic, GPE).' },
      { term: 'Energy transfer', definition: 'Movement of energy from one store to another.' },
      { term: 'Conservation of energy', definition: 'Energy cannot be created or destroyed — only transferred.' },
    ],
    practice: {
      warmUp: [
        cQ('p1-1.w1', 'A moving cyclist has which energy store?', ['Elastic', 'Kinetic', 'Magnetic', 'Nuclear'], 'Kinetic', 'Movement', 'KE', 'Standard'),
        cQ('p1-1.w2', 'A book on a shelf has:', ['Kinetic', 'GPE', 'Sound', 'Chemical'], 'GPE', 'Raised object', 'Above ground', 'Gravitational potential'),
      ],
      level1: [
        cQ('p1-1.1', 'Energy can be:', ['Created', 'Destroyed', 'Only transferred', 'Multiplied'], 'Only transferred', 'Conservation law', 'Stays the same total', 'Just moves around'),
      ],
      level2: [
        tQ('p1-1.2', 'Total energy of a closed system stays the same — this is the law of ___.', 'conservation', '12 letters', 'C___________', 'Of energy'),
      ],
    },
  }),

  gt('gcse-p1-2', 'kinetic-gpe-elastic-energy', 'Calculating KE, GPE & EPE', {
    pageRef: 'P1.2',
    intro: 'Three key formulae. Kinetic energy: Eₖ = ½ m v² (mass in kg, velocity in m/s, energy in J). Gravitational potential energy: Eₚ = m g h (h = height change in m, g = 9.8 N/kg). Elastic potential energy: Eₑ = ½ k e² (k = spring constant N/m, e = extension in m). Use these to track energy transfers — e.g. a ball falls: GPE → KE; a stretched spring: work done → EPE.',
    keyPoints: [
      'KE: Eₖ = ½ m v² (joules)',
      'GPE: Eₚ = m g h (g = 9.8 N/kg)',
      'EPE: Eₑ = ½ k e²',
      'Energy unit always joule (J)',
      'Use kg, m, s units throughout',
    ],
    we: [
      {
        problem: 'A 0.5 kg ball is dropped from 4 m. Calculate its speed just before landing. (g = 9.8 N/kg)',
        steps: [
          { step: 1, instruction: 'GPE at top → KE at bottom', working: 'm g h = ½ m v²' },
          { step: 2, instruction: 'Cancel mass from both sides', working: 'g h = ½ v²' },
          { step: 3, instruction: 'Solve for v', working: 'v² = 2 × 9.8 × 4 = 78.4 → v = √78.4' },
          { step: 4, instruction: 'Evaluate', working: 'v ≈ 8.85 m/s' },
        ],
        answer: 'v ≈ 8.85 m/s',
        commonMistake: 'Forgetting the factor of 2 when rearranging ½ m v² for v² = 2 g h.',
      },
    ],
    examQ: {
      prompt: 'A 60 kg cyclist is moving at 8.0 m/s. Calculate her kinetic energy. Show working. (3 marks)',
      maxMarks: 3,
      markSchemePoints: [
        'Eₖ = ½ m v² (correct formula recalled)',
        'Substitute: ½ × 60 × 8.0² = ½ × 60 × 64',
        'Eₖ = 1920 J',
      ],
    },
    examinerInsights: {
      whyMarksLost: [
        'Forgetting to SQUARE v. ½ × 60 × 8 = 240 J is wrong but very common.',
        'Putting the answer in kJ when J is requested — always match the unit asked for.',
        'Not showing the formula on its own line — most mark schemes give a formula-recall mark even if numbers go wrong.',
      ],
      whatExaminersWant: [
        'Three lines: formula, substitution, evaluation. One mark each.',
        'Units on the final answer (J for energy).',
        'A clear v² shown, not v.',
      ],
      commonTraps: [
        'Confusing kinetic energy formula (½ m v²) with momentum (m v) — both involve mass and velocity.',
        'Using grams instead of kg — always convert before substituting.',
        'Squaring the whole thing (½ m v)² — the squaring is ON v ONLY.',
      ],
      gradeNineMove: 'Even on a 2-mark calculation, write out the formula on a separate line. It costs you 5 seconds and protects 1 mark whenever your arithmetic slips.',
    },
    glossary: [
      { term: 'Kinetic energy', definition: 'Energy of a moving object: ½ m v².' },
      { term: 'GPE', definition: 'Gravitational potential energy: m g h.' },
      { term: 'EPE', definition: 'Elastic potential energy stored when a spring is stretched: ½ k e².' },
    ],
    practice: {
      warmUp: [
        nQ('p1-2.w1', 'KE of 2 kg moving at 5 m/s? (J)', '25', '½ × 2 × 25', '½ m v²', '2 digits'),
        nQ('p1-2.w2', 'GPE of 4 kg lifted 5 m (g=10)? (J)', '200', 'mgh', '4 × 10 × 5', '3 digits'),
      ],
      level1: [
        nQ('p1-2.1', 'KE of 1500 kg car at 20 m/s? (J)', '300000', '½ × 1500 × 400', '6 zeros', 'Big number'),
        nQ('p1-2.2', 'A 0.5 kg book lifted 2 m. GPE? (g=10) (J)', '10', '0.5 × 10 × 2', 'mgh', '2 digits'),
      ],
      level2: [
        nQ('p1-2.3', 'Spring stretched 0.05 m, k=200 N/m. EPE? (J)', '0.25', '½ × 200 × 0.05²', '½ k e²', 'Decimal'),
      ],
    },
  }),

  gt('gcse-p1-3', 'specific-heat-capacity', 'Specific Heat Capacity (RP1)', {
    pageRef: 'P1.3',
    intro: 'Specific heat capacity (c) = energy needed to raise 1 kg of a substance by 1°C. Formula: ΔE = m c Δθ. Water has a high SHC (4200 J/kg°C) — needs lots of energy to heat. Metals have low SHC (~400-900). High SHC = good for heating systems and storing thermal energy. RP1 measures the SHC of a metal block by measuring electrical energy input and resulting temperature rise.',
    keyPoints: [
      'ΔE = m c Δθ (energy in J, mass in kg, c in J/kg°C, Δθ in °C)',
      'Water: c = 4200 J/kg°C (very high)',
      'Aluminium: ~900; copper: ~385',
      'High SHC → resists temperature change well',
      'Insulation reduces wasted heat — keeps the m c Δθ relationship valid',
    ],
    we: {
      problem: 'How much energy is needed to heat 0.5 kg of water from 20°C to 100°C? (c = 4200 J/kg°C)',
      steps: [
        { step: 1, instruction: 'Δθ = 100 - 20', working: '= 80°C' },
        { step: 2, instruction: 'ΔE = m c Δθ', working: '0.5 × 4200 × 80' },
        { step: 3, instruction: 'Evaluate', working: '= 168,000 J' },
      ],
      answer: '168,000 J = 168 kJ',
    },
    rp: {
      title: 'Required Practical: An investigation to determine the specific heat capacity of a material',
      aqaRef: 'AQA Required Practical (Physics) 1',
      aim: 'Measure the specific heat capacity of a 1 kg aluminium block.',
      method: [
        'Weigh the metal block. Record mass.',
        'Insulate the block (e.g. with cotton wool) to reduce heat loss.',
        'Insert a 12 V immersion heater into the centre hole; connect via joulemeter.',
        'Insert a thermometer into the second hole. Record initial temperature.',
        'Switch on heater for 10 minutes; record final temperature and energy supplied (Joules).',
        'Calculate c = ΔE ÷ (m × Δθ).',
      ],
      safety: 'Heater gets hot — handle by stand only; don\'t touch directly. Mains electricity — keep work surface dry.',
      tips: [
        'Insulating reduces heat loss to surroundings — gives a more accurate c.',
        'Maximum temperature is reached SHORTLY AFTER switching off — heat continues to spread through block.',
      ],
    },
    glossary: [
      { term: 'Specific heat capacity', definition: 'Energy required to raise 1 kg of a substance by 1°C. Unit: J/kg°C.' },
    ],
    practice: {
      warmUp: [
        nQ('p1-3.w1', 'Energy to heat 1 kg water by 1°C? (J)', '4200', 'SHC of water', '1 × 4200 × 1', '4 digits'),
      ],
      level1: [
        nQ('p1-3.1', 'Energy to heat 2 kg water from 20 → 60°C? (c=4200) (J)', '336000', 'm c Δθ; 2×4200×40', '40°C rise', 'Big number'),
      ],
      level2: [
        nQ('p1-3.2', '5000 J supplied; 0.5 kg metal heats 50→60°C. Calculate c.', '1000', 'c = ΔE/(m Δθ)', '5000/(0.5 × 10)', '4 digits'),
      ],
    },
  }),

  gt('gcse-p1-4', 'power-efficiency', 'Power & Efficiency', {
    pageRef: 'P1.4',
    intro: 'Power = energy transferred per second. P = E ÷ t (watts, joules, seconds). 1 W = 1 J/s. Power can also be calculated as work done per second. Efficiency = useful energy out ÷ total energy in (or useful power ÷ total power). Often given as %. Wasted energy is usually transferred to thermal energy of surroundings (often by friction). Better insulation, lubrication and aerodynamic design reduce waste.',
    keyPoints: [
      'P = E ÷ t (watts = J/s)',
      'Efficiency = useful out ÷ total in (× 100 for %)',
      'Wasted energy → thermal store of surroundings (heat)',
      'Reduce waste with: insulation, lubrication, streamlining',
    ],
    we: {
      problem: 'A 60 W lamp is on for 2 minutes. How much energy does it transfer?',
      steps: [
        { step: 1, instruction: 'Convert time to seconds', working: '2 min = 120 s' },
        { step: 2, instruction: 'E = P × t', working: '60 × 120' },
        { step: 3, instruction: 'Evaluate', working: '= 7200 J' },
      ],
      answer: '7200 J = 7.2 kJ',
      commonMistake: 'Using time in minutes instead of seconds — power is per SECOND.',
    },
    glossary: [
      { term: 'Power', definition: 'Rate of energy transfer; P = E ÷ t. Unit: watt (W).' },
      { term: 'Efficiency', definition: 'Useful energy ÷ total energy.' },
    ],
    examQ: {
      prompt: 'A motor draws 240 J of electrical energy in 1 minute. It does 180 J of useful work in lifting a load. Calculate (i) power input, (ii) efficiency. (4 marks)',
      maxMarks: 4,
      markSchemePoints: [
        'P = E ÷ t = 240 ÷ 60 = 4.0 W',
        'Efficiency = useful ÷ total',
        '= 180 ÷ 240',
        '= 0.75 (or 75%)',
      ],
    },
    practice: {
      warmUp: [
        nQ('p1-4.w1', '100 W lamp for 60 s. Energy used? (J)', '6000', 'E = P × t', '100 × 60', '4 digits'),
      ],
      level1: [
        nQ('p1-4.1', 'Useful energy 80 J; total energy 100 J. Efficiency? (decimal)', '0.8', '80/100', 'Useful/total', 'Decimal'),
        nQ('p1-4.2', 'Energy 1500 J in 5 s. Power? (W)', '300', '1500/5', 'P = E/t', '3 digits'),
      ],
      level2: [
        nQ('p1-4.3', 'A car engine: useful 30 kJ from 100 kJ fuel. % efficiency?', '30', '30/100 × 100', '30%', '2 digits'),
      ],
    },
  }),

  gt('gcse-p1-5', 'energy-resources', 'National & Global Energy Resources', {
    pageRef: 'P1.5',
    intro: 'Energy resources fall into two camps. Renewable: replenished as fast as used — solar, wind, water (hydro/tidal/wave), geothermal, biofuels. Non-renewable: finite — fossil fuels (coal, oil, gas), nuclear (uranium). Most UK electricity comes from a mix: gas, nuclear, wind, solar, biomass. Renewable advantages: low/zero CO₂; sustainable. Disadvantages: variability (no wind = no power), large land area, expensive to install. Demand peaks: morning, evening, cold spells.',
    keyPoints: [
      'Renewable: solar, wind, hydro, tidal, wave, geothermal, biofuel',
      'Non-renewable: coal, oil, gas, nuclear (uranium)',
      'UK electricity mix: gas, nuclear, wind, solar, biomass',
      'Renewables: low CO₂ but variable + costly to install',
      'Fossil fuels: high CO₂ but cheap + reliable',
    ],
    glossary: [
      { term: 'Renewable', definition: 'Replenished as fast as used (solar, wind, hydro).' },
      { term: 'Non-renewable', definition: 'Finite resource (fossil fuels, nuclear).' },
      { term: 'Biofuel', definition: 'Fuel made from recently living matter (e.g. ethanol, biodiesel).' },
    ],
    practice: {
      warmUp: [
        cQ('p1-5.w1', 'Which is renewable?', ['Coal', 'Natural gas', 'Wind', 'Uranium'], 'Wind', 'Air movement', 'Replenished', 'Always blowing'),
      ],
      level1: [
        cQ('p1-5.1', 'A disadvantage of solar?', ['Toxic emissions', 'Variable — no sun at night', 'Carbon dioxide', 'Loud'], 'Variable — no sun at night', 'Intermittent', 'Need backup', 'Storage problem'),
      ],
      level2: [
        cQ('p1-5.2', 'Why are fossil fuels still used?', ['Free', 'Cheap, reliable, energy-dense', 'No CO₂', 'Renewable'], 'Cheap, reliable, energy-dense', 'Cost & reliability', 'Reasons for current use', 'Trade-off with environment'),
      ],
    },
  }),
];

// =============================================================
// P2 — Electricity
// =============================================================
export const p2ElectricityTopics = [
  gt('gcse-p2-1', 'circuit-basics-current-pd', 'Circuit Basics: Current, p.d. & Resistance', {
    pageRef: 'P2.1',
    intro: 'Electric current (I) is the flow of charge — measured in amperes (A). 1 A = 1 coulomb of charge per second: Q = I t. Potential difference (p.d., V) is the energy transferred per coulomb of charge passing — volts (V). Resistance (R) opposes current flow — ohms (Ω). Ohm\'s law: V = I R. Components in SERIES: same current, p.d. shares. PARALLEL: same p.d., current shares. Ammeter in series, voltmeter in parallel.',
    keyPoints: [
      'Current I in amperes (A); p.d. V in volts; resistance R in ohms (Ω)',
      'Q = I t (charge in coulombs)',
      'V = I R (Ohm\'s law)',
      'Series: same I, V sums to total; total R = sum of R',
      'Parallel: same V, I sums to total; total R is LESS than smallest individual R',
    ],
    we: [
      {
        problem: 'A bulb has 0.5 A flowing through it at 6 V. Calculate its resistance.',
        steps: [
          { step: 1, instruction: 'Rearrange Ohm\'s law', working: 'R = V ÷ I' },
          { step: 2, instruction: 'Substitute', working: '6 ÷ 0.5' },
          { step: 3, instruction: 'Evaluate', working: '= 12 Ω' },
        ],
        answer: 'R = 12 Ω',
      },
    ],
    examQ: {
      prompt: 'A circuit has a 9 V battery and two resistors in series: 4 Ω and 8 Ω. Calculate (i) total resistance, (ii) current through the circuit. (3 marks)',
      maxMarks: 3,
      markSchemePoints: [
        'Total R in series = R₁ + R₂ = 4 + 8 = 12 Ω',
        'I = V ÷ R = 9 ÷ 12',
        'I = 0.75 A',
      ],
    },
    glossary: [
      { term: 'Current (I)', definition: 'Rate of flow of charge; A = C/s.' },
      { term: 'Potential difference (p.d.)', definition: 'Energy transferred per unit charge; volts.' },
      { term: 'Resistance (R)', definition: 'Opposition to current; ohms.' },
      { term: 'Ohm\'s law', definition: 'V = I × R for ohmic conductors.' },
    ],
    practice: {
      warmUp: [
        nQ('p2-1.w1', 'Current = 2 A; p.d. = 12 V. Resistance? (Ω)', '6', 'V/I', '12/2', '1 digit'),
      ],
      level1: [
        nQ('p2-1.1', 'Series: R₁ = 5 Ω, R₂ = 7 Ω. Total R?', '12', 'Sum in series', '5+7', '2 digits'),
        nQ('p2-1.2', 'Charge if 0.5 A flows for 60 s? (C)', '30', 'Q = It', '0.5 × 60', '2 digits'),
      ],
      level2: [
        nQ('p2-1.3', 'V = 24 V; R = 8 Ω. Current? (A)', '3', 'V/R', '24/8', '1 digit'),
      ],
    },
  }),

  gt('gcse-p2-2', 'iv-characteristics-rp', 'I-V Characteristics (RP2)', {
    pageRef: 'P2.2',
    intro: 'Plot current against p.d. for different components: ohmic conductor (e.g. resistor at constant T) — straight line through origin. Filament lamp — S-shape (resistance rises as filament heats up). Diode — only conducts in one direction. Used to investigate how components behave electrically.',
    keyPoints: [
      'Ohmic conductor: linear I vs V through origin (constant R)',
      'Filament lamp: S-curve (R increases with T as filament heats)',
      'Diode: very low I until threshold; ~0 in reverse direction',
      'Use a variable resistor to change p.d. across the component',
    ],
    rp: {
      title: 'Required Practical: Use circuit diagrams to set up and check appropriate circuits to investigate the factors affecting the resistance of electrical circuits',
      aqaRef: 'AQA Required Practical (Physics) 3',
      aim: 'Investigate I vs V relationship for a resistor, filament lamp and diode.',
      method: [
        'Build the circuit: cell, ammeter (in series with component), voltmeter (in parallel with component), variable resistor.',
        'Vary the variable resistor — record matching current and p.d. values.',
        'Repeat for each component (resistor, lamp, diode).',
        'Plot I (y-axis) against V (x-axis).',
        'For diode: reverse leads to test reverse-bias direction.',
      ],
      safety: 'Don\'t exceed rated current — components can burn out. Switch off between adjustments.',
      tips: [
        'Take readings going up AND down — average to reduce error.',
        'Diode protection: use a series resistor to limit current.',
      ],
    },
    glossary: [
      { term: 'Ohmic conductor', definition: 'Resistance is constant; current is directly proportional to p.d.' },
      { term: 'Filament lamp', definition: 'Lamp where resistance rises with temperature.' },
      { term: 'Diode', definition: 'Component that allows current in one direction only.' },
    ],
    practice: {
      warmUp: [
        cQ('p2-2.w1', 'A straight line through origin on I-V means:', ['Filament lamp', 'Ohmic conductor', 'Diode', 'Open circuit'], 'Ohmic conductor', 'Constant R', 'V = IR linear', 'Definition'),
      ],
      level1: [
        cQ('p2-2.1', 'Why does a filament lamp have curved I-V?', ['Faulty', 'Filament heats; resistance rises', 'Diode effect', 'Battery flat'], 'Filament heats; resistance rises', 'Temperature affects R', 'Tungsten filament', 'Standard answer'),
      ],
      level2: [
        cQ('p2-2.2', 'A diode allows current:', ['Always', 'In one direction only', 'Never', 'Bidirectional with limits'], 'In one direction only', 'Forward bias only', 'Rectifier', 'Asymmetric I-V'),
      ],
    },
  }),

  gt('gcse-p2-3', 'mains-electricity', 'Mains Electricity & The Three-Pin Plug', {
    pageRef: 'P2.3',
    intro: 'UK mains: 230 V, 50 Hz, alternating current (a.c.). Direct current (d.c.) flows in one direction (battery); a.c. reverses direction continually. Three-pin plug: live (brown, 230 V), neutral (blue, 0 V), earth (green/yellow stripe, safety only). Earth wire + fuse: if the live wire touches the metal case, current goes to earth instead of through user; fuse melts and breaks circuit. Double insulation = no metal exposed → no earth needed.',
    keyPoints: [
      'UK: 230 V, 50 Hz, a.c.',
      'Live (brown) carries current; Neutral (blue) returns; Earth (green/yellow) safety',
      'Fuse: melts if current too high; breaks circuit',
      'Earth + fuse work TOGETHER to prevent shock',
      'Double-insulated appliances: no exposed metal → earth not needed',
    ],
    glossary: [
      { term: 'a.c.', definition: 'Alternating current — direction reverses (UK 50 Hz).' },
      { term: 'd.c.', definition: 'Direct current — flows one way only (battery).' },
      { term: 'Earth wire', definition: 'Safety wire connecting metal case to ground.' },
      { term: 'Fuse', definition: 'Wire that melts when current exceeds rating, breaking circuit.' },
    ],
    practice: {
      warmUp: [
        nQ('p2-3.w1', 'UK mains voltage (V)?', '230', 'UK standard', 'Around 230', '3 digits'),
        cQ('p2-3.w2', 'Colour of the live wire?', ['Blue', 'Green/yellow', 'Brown', 'Black'], 'Brown', 'Carries current', 'Hot', 'Standard'),
      ],
      level1: [
        cQ('p2-3.1', 'Frequency of UK mains?', ['50 Hz', '60 Hz', '230 Hz', '100 Hz'], '50 Hz', 'UK standard', 'a.c.', 'Cycles/sec'),
      ],
      level2: [
        cQ('p2-3.2', 'Earth wire purpose:', ['Carry current normally', 'Carry current to ground if fault', 'Decoration', 'Improve efficiency'], 'Carry current to ground if fault', 'Safety', 'With fuse', 'Stops shock'),
      ],
    },
  }),

  gt('gcse-p2-4', 'electrical-power-energy', 'Electrical Power & Energy in Circuits', {
    pageRef: 'P2.4',
    intro: 'Electrical power: P = V I (watts) and P = I² R. Energy transferred: E = V I t = P t. Useful for calculating cost: kWh × price. Higher current = more heating in wires (P = I² R) — that\'s why high-voltage transmission is used: same power but much smaller current → less heating loss.',
    keyPoints: [
      'P = V I',
      'P = I² R (heating in any conductor)',
      'E = P t = V I t',
      'Cost = energy in kWh × price per kWh',
      'High V transmission: smaller I → smaller I²R losses',
    ],
    we: {
      problem: 'A 2.5 kW kettle is used for 0.5 hours. If electricity costs 30 p per kWh, calculate the cost.',
      steps: [
        { step: 1, instruction: 'Convert P to kW (already)', working: 'P = 2.5 kW' },
        { step: 2, instruction: 'Energy in kWh = P × t (in hours)', working: '2.5 × 0.5 = 1.25 kWh' },
        { step: 3, instruction: 'Cost', working: '1.25 × 30 = 37.5 p' },
      ],
      answer: '37.5 p (£0.375)',
    },
    glossary: [
      { term: 'kWh', definition: 'Kilowatt-hour — unit of energy used for billing.' },
    ],
    practice: {
      warmUp: [
        nQ('p2-4.w1', 'Power of an appliance: V = 230, I = 2 A. P (W)?', '460', 'P = VI', '230 × 2', '3 digits'),
      ],
      level1: [
        nQ('p2-4.1', '3 kW heater on for 2 hrs. Energy (kWh)?', '6', 'P × t (in h)', '3 × 2', '1 digit'),
      ],
      level2: [
        nQ('p2-4.2', 'Why use high voltage transmission? Lower ___ wastes less energy. (1 word)', 'current', '7 letters', 'I in P=I²R', 'C______'),
      ],
    },
  }),
];

// =============================================================
// P3 — Particle Model of Matter
// =============================================================
export const p3ParticleModelTopics = [
  gt('gcse-p3-1', 'density-of-materials', 'Density (RP3)', {
    pageRef: 'P3.1',
    intro: 'Density (ρ) = mass ÷ volume. Unit: kg/m³ or g/cm³. Solids/liquids: dense, particles tightly packed. Gases: very low density, particles far apart. Measure regular shapes by length × width × height; irregular shapes by water displacement (Eureka can or measuring cylinder).',
    keyPoints: [
      'ρ = m ÷ V',
      'Water = 1000 kg/m³ = 1 g/cm³',
      'Regular shapes: l × w × h',
      'Irregular shapes: water displacement',
      'Will it float? Density less than water → floats',
    ],
    we: {
      problem: 'A 50 cm³ block has mass 135 g. Calculate density and predict whether it floats in water.',
      steps: [
        { step: 1, instruction: 'ρ = m ÷ V', working: '135 ÷ 50' },
        { step: 2, instruction: 'Evaluate', working: '= 2.7 g/cm³' },
        { step: 3, instruction: 'Compare to water (1.0 g/cm³)', working: '2.7 > 1.0 → SINKS' },
      ],
      answer: 'ρ = 2.7 g/cm³; it sinks (more dense than water)',
    },
    rp: {
      title: 'Required Practical: Use of an appropriate apparatus to make and record the measurements needed to determine the densities of regular and irregular solid objects and liquids',
      aqaRef: 'AQA Required Practical (Physics) 5',
      aim: 'Determine densities of (i) a regular solid block, (ii) an irregular stone, (iii) a liquid.',
      method: [
        'Regular solid: measure mass on balance; measure length × width × height with ruler.',
        'Irregular solid: measure mass; lower into measuring cylinder of water; volume = rise in water level.',
        'Liquid: zero balance with empty measuring cylinder; pour known volume; record mass; ρ = m ÷ V.',
      ],
      safety: 'Glass measuring cylinder is fragile. Sharp-edged solids — handle with care.',
      tips: [
        'For irregular solid: lower gently to avoid splashing — water leaving the cylinder = error.',
        'Repeat measurements 3 times and use the mean.',
      ],
    },
    glossary: [
      { term: 'Density (ρ)', definition: 'Mass per unit volume; ρ = m/V.' },
    ],
    practice: {
      warmUp: [
        nQ('p3-1.w1', '20 g in 10 cm³. Density (g/cm³)?', '2', 'm/V', '20/10', '1 digit'),
        nQ('p3-1.w2', 'Density of water (g/cm³)?', '1', 'Reference', 'Pure water', '1 digit'),
      ],
      level1: [
        cQ('p3-1.1', 'A material with density 0.7 g/cm³ in water:', ['Sinks', 'Floats', 'Mid-water', 'Dissolves'], 'Floats', 'Less dense', 'Compared to water', 'Standard rule'),
      ],
      level2: [
        nQ('p3-1.2', 'Block 5 cm × 4 cm × 2 cm, mass 320 g. Density (g/cm³)?', '8', 'V = 40 cm³; 320/40', '8 g/cm³', '1 digit'),
      ],
    },
  }),

  gt('gcse-p3-2', 'changes-of-state-internal-energy', 'Changes of State & Internal Energy', {
    pageRef: 'P3.2',
    intro: 'Internal energy = total kinetic + potential energy of all particles. Heating raises internal energy by either (i) increasing T (particles vibrate / move faster) or (ii) changing state (overcoming forces between particles, T stays constant). State changes are physical (mass conserved, reversible). Specific latent heat: energy to change 1 kg from solid to liquid (fusion) or liquid to gas (vaporisation), with NO temperature change.',
    keyPoints: [
      'Internal energy = total KE + PE of particles',
      'Heating → temperature rise OR state change',
      'During state change, T is constant; energy goes into breaking forces',
      'E = m L (latent heat); L = specific latent heat',
      'Lf = fusion (s↔l); Lv = vaporisation (l↔g) — Lv > Lf usually',
    ],
    we: {
      problem: 'How much energy is needed to melt 0.2 kg of ice at 0°C? (Lf of ice = 334,000 J/kg)',
      steps: [
        { step: 1, instruction: 'E = m L', working: '0.2 × 334,000' },
        { step: 2, instruction: 'Evaluate', working: '= 66,800 J' },
      ],
      answer: '66,800 J',
    },
    glossary: [
      { term: 'Internal energy', definition: 'Total energy stored inside a system by its particles.' },
      { term: 'Specific latent heat', definition: 'Energy needed to change state of 1 kg, with no temperature change.' },
    ],
    practice: {
      warmUp: [
        cQ('p3-2.w1', 'During melting, temperature:', ['Rises', 'Falls', 'Stays the same', 'Drops to 0'], 'Stays the same', 'Energy goes into bonds', 'Phase change', 'No T change'),
      ],
      level1: [
        nQ('p3-2.1', '1 kg ice at 0°C → water at 0°C. Energy needed if Lf = 334,000 J/kg? (J)', '334000', '1 × 334,000', '6 digits', 'Just E=mL'),
      ],
      level2: [
        cQ('p3-2.2', 'Why is Lv > Lf usually?', ['Liquid → gas: must completely separate particles', 'Solid is harder', 'Less work to overcome', 'No reason'], 'Liquid → gas: must completely separate particles', 'More forces broken', 'Bigger separation', 'Standard reason'),
      ],
    },
  }),

  gt('gcse-p3-3', 'particle-pressure-gas', 'Particle Motion & Gas Pressure', {
    pageRef: 'P3.3',
    intro: 'Gas pressure comes from particles colliding with container walls. Each collision exerts a tiny force; many collisions create pressure. ↑ T → particles move faster → harder + more frequent collisions → higher pressure (at constant volume). At constant T: ↓ V → particles closer → more collisions per second → higher P. Boyle\'s law: P₁ V₁ = P₂ V₂.',
    keyPoints: [
      'Pressure: average force per unit area from particle collisions',
      '↑ Temperature → ↑ pressure (at constant V)',
      'Boyle\'s law (constant T): P₁ V₁ = P₂ V₂',
      'Pressure × volume = constant for fixed mass at fixed T',
    ],
    we: {
      problem: 'A gas occupies 0.5 m³ at 100 kPa. It is compressed at constant T to 0.2 m³. New pressure?',
      steps: [
        { step: 1, instruction: 'Boyle: P₁V₁ = P₂V₂', working: '100 × 0.5 = P₂ × 0.2' },
        { step: 2, instruction: 'Solve for P₂', working: 'P₂ = 50 ÷ 0.2 = 250 kPa' },
      ],
      answer: 'P₂ = 250 kPa',
    },
    glossary: [
      { term: 'Pressure', definition: 'Force per unit area from particle collisions.' },
      { term: 'Boyle\'s law', definition: 'For fixed mass + T: PV = constant.' },
    ],
    practice: {
      warmUp: [
        cQ('p3-3.w1', 'Heating a sealed gas at constant V:', ['↓ pressure', '↑ pressure', 'No change', 'Negative pressure'], '↑ pressure', 'More energetic collisions', 'Faster particles', 'More force'),
      ],
      level1: [
        nQ('p3-3.1', 'P₁ = 200 kPa, V₁ = 0.4 m³. New V at 100 kPa? (m³)', '0.8', 'P₁V₁/P₂', '80/100', 'Decimal'),
      ],
      level2: [
        cQ('p3-3.2', 'Compressing a gas at constant T:', ['↑ pressure', '↓ pressure', 'No change', 'Negative T'], '↑ pressure', 'Fewer particles per area? No — more collisions/sec', 'Boyle', 'Inverse relationship'),
      ],
    },
  }),
];

// =============================================================
// P4 — Atomic Structure
// =============================================================
export const p4AtomicStructureTopics = [
  gt('gcse-p4-1', 'atom-isotopes-history', 'Atomic Structure & Models', {
    pageRef: 'P4.1',
    intro: 'Atom: nucleus (protons + neutrons) and electrons in shells. Atomic number Z = protons (defines element). Mass number A = protons + neutrons. Isotopes: same Z, different N. Models evolved: Dalton solid sphere → Thomson plum pudding → Rutherford nuclear (alpha-scattering: most empty space, tiny dense positive nucleus) → Bohr (electrons in fixed shells, after E.g. line spectra evidence) → Chadwick (1932 — neutron).',
    keyPoints: [
      'Atomic radius ~ 10⁻¹⁰ m; nucleus ~ 10⁻¹⁴ m',
      'Plum pudding (Thomson, 1897)',
      'Rutherford (1909): alpha scattering → nuclear model',
      'Bohr (1913): electrons in fixed energy levels',
      'Chadwick (1932): neutrons',
    ],
    glossary: [
      { term: 'Isotope', definition: 'Same element, different number of neutrons.' },
      { term: 'Plum pudding model', definition: 'Thomson\'s atom: positive sphere with embedded electrons.' },
      { term: 'Alpha scattering', definition: 'Rutherford experiment that revealed the small dense nucleus.' },
    ],
    practice: {
      warmUp: [
        cQ('p4-1.w1', 'Isotopes have same:', ['Mass number', 'Number of neutrons', 'Atomic number', 'Electrons'], 'Atomic number', 'Same element', 'Same Z', 'Different N'),
      ],
      level1: [
        cQ('p4-1.1', 'Rutherford\'s experiment showed:', ['Atom is solid', 'Plum pudding correct', 'Tiny dense positive nucleus', 'Electrons free'], 'Tiny dense positive nucleus', 'Most α passed through', 'Few deflected', 'Empty space'),
      ],
      level2: [
        tQ('p4-1.2', 'Particle discovered by Chadwick (1932): ___.', 'neutron', '7 letters', 'No charge', 'N______'),
      ],
    },
  }),

  gt('gcse-p4-2', 'radioactive-decay', 'Radioactive Decay & Nuclear Equations', {
    pageRef: 'P4.2',
    intro: 'Some unstable nuclei DECAY randomly, emitting one of: alpha (α — helium nucleus, 2p+2n, very ionising, stopped by paper), beta (β — fast electron from neutron→proton, stopped by aluminium), gamma (γ — high-energy EM wave, stopped by thick lead/concrete), neutron emission. Activity = decays per second (becquerels, Bq). Count rate = detected per second (depends on detector). Decay is random — can\'t predict individual nucleus, but bulk follows half-life.',
    keyPoints: [
      'α = ²He nucleus (2p, 2n, +2)',
      'β = electron from n → p (+ β⁻)',
      'γ = high-frequency EM wave',
      'α stops at paper; β at aluminium; γ at thick lead',
      'Decay is random — can\'t predict individual atoms',
      'Activity (Bq) = decays per second',
    ],
    we: {
      problem: 'Complete the alpha decay: ²²⁶Ra → ?? + ⁴He',
      steps: [
        { step: 1, instruction: 'Mass number balance', working: '226 = X + 4 → X = 222' },
        { step: 2, instruction: 'Atomic number balance (Ra=88, He=2)', working: '88 = Y + 2 → Y = 86' },
        { step: 3, instruction: 'Element with Z=86 is radon (Rn)', working: 'Daughter = ²²²Rn' },
      ],
      answer: '²²⁶Ra → ²²²Rn + ⁴He',
    },
    glossary: [
      { term: 'Alpha (α)', definition: 'Helium-4 nucleus emitted by some heavy nuclei.' },
      { term: 'Beta (β)', definition: 'High-energy electron emitted when a neutron becomes a proton.' },
      { term: 'Gamma (γ)', definition: 'High-frequency electromagnetic radiation.' },
      { term: 'Activity', definition: 'Decays per second; unit becquerel (Bq).' },
    ],
    practice: {
      warmUp: [
        cQ('p4-2.w1', 'Most ionising radiation:', ['Alpha', 'Beta', 'Gamma', 'X-ray'], 'Alpha', 'Largest, most charge', 'Heavy', 'Stops at paper'),
      ],
      level1: [
        cQ('p4-2.1', 'What stops gamma rays?', ['Paper', 'Skin', 'Aluminium foil', 'Thick lead/concrete'], 'Thick lead/concrete', 'Most penetrating', 'Need dense material', 'Standard'),
      ],
      level2: [
        tQ('p4-2.2', 'A neutron becoming a proton emits a ___ particle.', 'beta', '4 letters', 'B___', 'Electron from nucleus'),
      ],
    },
  }),

  gt('gcse-p4-3', 'half-life', 'Half-Life', {
    pageRef: 'P4.3',
    intro: 'Half-life is the time for either: (i) the number of undecayed nuclei to halve, or (ii) the activity to halve. Different isotopes have wildly different half-lives (carbon-14: 5,730 years; uranium-238: 4.5 billion years; radon-220: 56 seconds). After n half-lives, fraction remaining = (1/2)ⁿ. Used for: dating archaeological samples, medical imaging, nuclear waste management.',
    keyPoints: [
      'Half-life: time for activity to halve',
      'After n half-lives: (1/2)ⁿ remaining',
      'Constant for a given isotope',
      'Carbon-14 half-life ≈ 5730 years (used for archaeological dating)',
    ],
    we: {
      problem: 'A radioactive isotope has half-life 6 hours. The initial activity is 800 Bq. What is the activity after 18 hours?',
      steps: [
        { step: 1, instruction: 'Number of half-lives', working: '18 ÷ 6 = 3' },
        { step: 2, instruction: 'Fraction remaining', working: '(1/2)³ = 1/8' },
        { step: 3, instruction: 'Activity remaining', working: '800 × 1/8 = 100 Bq' },
      ],
      answer: '100 Bq',
    },
    glossary: [
      { term: 'Half-life', definition: 'Time for activity (or number of undecayed nuclei) to halve.' },
    ],
    practice: {
      warmUp: [
        nQ('p4-3.w1', 'A sample halves every 5 min. After 10 min, what fraction remains? (decimal)', '0.25', '2 half-lives', '1/4', 'Decimal'),
      ],
      level1: [
        nQ('p4-3.1', 'Initial activity 1600 Bq, half-life 1 hr. Activity after 4 hrs (Bq)?', '100', '4 halvings: 1/16', '1600/16', '3 digits'),
        nQ('p4-3.2', 'After 3 half-lives, fraction remaining (decimal)?', '0.125', '(1/2)³', '1/8', 'Decimal'),
      ],
      level2: [
        nQ('p4-3.3', 'Sample at 240 Bq. After 3 hrs at 30 Bq. Half-life (hours)?', '1', '240→120→60→30 = 3 halvings in 3 hr', '3 hrs / 3 halvings', '1 digit'),
      ],
    },
  }),
];

// =============================================================
// P5 — Forces
// =============================================================
export const p5ForcesTopics = [
  gt('gcse-p5-1', 'forces-vectors-resultant', 'Forces, Vectors & Resultant Force', {
    pageRef: 'P5.1',
    intro: 'A force is a push or pull, measured in newtons (N). VECTOR quantities have magnitude AND direction (force, velocity, acceleration). SCALAR quantities have magnitude only (mass, distance, speed, energy). When multiple forces act on an object, find the RESULTANT force by adding them taking direction into account. If forces balance → no resultant → no change in motion.',
    keyPoints: [
      'Force = push/pull in newtons',
      'Vector: magnitude + direction (e.g. force, velocity)',
      'Scalar: magnitude only (e.g. mass, speed)',
      'Resultant force = single force with same effect as all combined',
      'Balanced (zero resultant): velocity unchanged (Newton 1)',
    ],
    glossary: [
      { term: 'Vector', definition: 'A quantity with both magnitude and direction.' },
      { term: 'Scalar', definition: 'A quantity with magnitude only.' },
      { term: 'Resultant force', definition: 'The single force equivalent to all forces acting.' },
    ],
    practice: {
      warmUp: [
        cQ('p5-1.w1', 'Mass is a:', ['Vector', 'Scalar', 'Force', 'Velocity'], 'Scalar', 'Magnitude only', 'No direction', 'kg'),
        nQ('p5-1.w2', 'Two forces in same direction: 5 N + 3 N. Resultant (N)?', '8', 'Add directly', '5+3', '1 digit'),
      ],
      level1: [
        nQ('p5-1.1', '10 N right, 4 N left. Resultant (N)?', '6', '10 - 4', 'Net = 6 right', '1 digit'),
      ],
      level2: [
        cQ('p5-1.2', 'Balanced forces mean:', ['No motion', 'Constant velocity', 'Acceleration', 'Force is zero'], 'Constant velocity', 'Newton 1st law', 'Could be moving', 'No acceleration'),
      ],
    },
  }),

  gt('gcse-p5-2', 'newton-laws', 'Newton\'s Three Laws', {
    pageRef: 'P5.2',
    intro: 'Newton\'s 1st law: an object at rest stays at rest, and a moving object continues at constant velocity, unless acted on by an unbalanced force. 2nd law: F = m × a (force = mass × acceleration). 3rd law: every action has an equal and opposite reaction (forces in pairs, on different objects, same type, equal magnitude, opposite direction). Inertial mass = m in F=ma; the "tendency to resist acceleration".',
    keyPoints: [
      '1st law: balanced forces → constant velocity (or rest)',
      '2nd law: F = m × a',
      '3rd law: equal and opposite force pairs (different objects)',
      'Inertial mass = F/a (resistance to acceleration)',
    ],
    we: [
      {
        problem: 'A 1500 kg car accelerates at 2 m/s². Calculate the resultant force.',
        steps: [
          { step: 1, instruction: 'F = m a', working: '1500 × 2' },
          { step: 2, instruction: 'Evaluate', working: '= 3000 N' },
        ],
        answer: 'F = 3000 N',
      },
    ],
    examQ: {
      prompt: 'A 70 kg cyclist accelerates from rest to 12 m/s in 6 s. Calculate (i) acceleration, (ii) resultant force on cyclist + bike. Total mass with bike = 90 kg. (4 marks)',
      maxMarks: 4,
      markSchemePoints: [
        'a = (v - u) ÷ t = 12 ÷ 6',
        'a = 2 m/s²',
        'F = m a = 90 × 2',
        'F = 180 N',
      ],
    },
    examinerInsights: {
      whyMarksLost: [
        'Using the cyclist mass (70 kg) instead of cyclist + bike (90 kg). Read the question carefully.',
        'Mixing up u and v in the acceleration formula. u = INITIAL velocity (0 here, "from rest").',
        'Skipping units — m/s² for acceleration, N for force.',
      ],
      whatExaminersWant: [
        'Formula written first, substitution second, answer third — for BOTH parts.',
        'Recognising "from rest" → u = 0.',
        'Final answer with the right unit — examiners check both.',
      ],
      commonTraps: [
        'Forgetting Newton\'s 2nd law uses RESULTANT force, not applied force.',
        'Calculating a in km/h instead of m/s² when given odd time units.',
        'Using mass × velocity (momentum) instead of mass × acceleration (force).',
      ],
      gradeNineMove: 'When a question gives multiple masses, circle the correct one before you start. A simple highlighting habit saves 1-2 marks per paper.',
    },
    glossary: [
      { term: 'Newton (N)', definition: 'Unit of force; 1 N = 1 kg m/s².' },
      { term: 'Inertia', definition: 'Tendency of an object to resist change in motion.' },
    ],
    practice: {
      warmUp: [
        nQ('p5-2.w1', 'F = 50 N, m = 10 kg. Acceleration (m/s²)?', '5', 'a = F/m', '50/10', '1 digit'),
        nQ('p5-2.w2', 'F = m a; m = 4 kg, a = 3 m/s². F? (N)', '12', '4 × 3', 'Direct multiply', '2 digits'),
      ],
      level1: [
        cQ('p5-2.1', 'Newton\'s 3rd law pair example:', ['A book on table: book\'s weight + table push back', 'A car accelerating', 'A free-falling stone', 'Earth orbiting sun'], 'A book on table: book\'s weight + table push back', 'Equal and opposite', 'Same type, different objects', 'Action-reaction'),
      ],
      level2: [
        nQ('p5-2.2', 'A 1200 kg car decelerates at 4 m/s². Braking force? (N)', '4800', 'F = ma', '1200 × 4', '4 digits'),
      ],
    },
  }),

  gt('gcse-p5-3', 'speed-velocity-acceleration', 'Speed, Velocity & Acceleration', {
    pageRef: 'P5.3',
    intro: 'Speed (scalar) = distance ÷ time. Velocity (vector) = speed in a specified direction. Acceleration = (change in velocity) ÷ time = (v - u) ÷ t. Distance-time graph: slope = speed; horizontal line = stationary. Velocity-time graph: slope = acceleration; horizontal line = constant velocity; AREA UNDER LINE = distance travelled. Equation of motion: v² = u² + 2 a s (no time needed).',
    keyPoints: [
      'speed = distance ÷ time',
      'velocity = speed in stated direction',
      'a = (v − u) ÷ t',
      'v² = u² + 2 a s',
      'Distance from v-t graph: area under line',
      'Acceleration from v-t: slope',
    ],
    we: [
      {
        problem: 'A car accelerates from 5 m/s to 25 m/s in 4 s. Calculate (i) acceleration, (ii) distance travelled.',
        steps: [
          { step: 1, instruction: 'a = (v−u)/t', working: '(25−5)/4 = 5 m/s²' },
          { step: 2, instruction: 'v² = u² + 2as → s = (v²−u²)/(2a)', working: '(625 - 25)/10 = 60 m' },
        ],
        answer: 'a = 5 m/s²; s = 60 m',
      },
    ],
    glossary: [
      { term: 'Velocity', definition: 'Speed in a stated direction (vector).' },
      { term: 'Acceleration', definition: 'Rate of change of velocity; m/s².' },
    ],
    practice: {
      warmUp: [
        nQ('p5-3.w1', '60 m in 10 s. Speed? (m/s)', '6', 'd/t', '60/10', '1 digit'),
        nQ('p5-3.w2', 'From 0 to 20 m/s in 4 s. a (m/s²)?', '5', '(20-0)/4', '20/4', '1 digit'),
      ],
      level1: [
        nQ('p5-3.1', 'Distance from area under v-t for 10 m/s constant for 6 s? (m)', '60', 'Rectangle: 10×6', 'Area = base × height', '2 digits'),
        nQ('p5-3.2', 'a = 2 m/s², u = 0, t = 5 s. v? (m/s)', '10', 'v = u + at', '0 + 2×5', '2 digits'),
      ],
      level2: [
        nQ('p5-3.3', 'u = 4 m/s, a = 3 m/s², s = 6 m. v² = ?', '52', 'v² = u² + 2as = 16 + 36', 'v² formula', '2 digits'),
      ],
    },
  }),

  gt('gcse-p5-4', 'stopping-distance', 'Stopping Distance & Reaction Time', {
    pageRef: 'P5.4',
    intro: 'Total stopping distance = thinking distance + braking distance. Thinking distance: while driver reacts (distance covered at original speed × reaction time, ~0.2-0.9 s). Braking distance: distance travelled once brakes applied. ↑ Speed → ↑ both. Affected by: tiredness, alcohol, drugs (thinking); wet/icy roads, worn tyres/brakes (braking). Hard braking → high force → potential brake fade or skid.',
    keyPoints: [
      'Stopping = thinking + braking',
      'Thinking distance ∝ speed',
      'Braking distance ∝ speed²',
      'Reaction-time factors: tiredness, alcohol, drugs, distractions',
      'Braking factors: wet/icy roads, worn tyres/brakes',
    ],
    glossary: [
      { term: 'Thinking distance', definition: 'Distance travelled during driver\'s reaction time before brakes applied.' },
      { term: 'Braking distance', definition: 'Distance travelled while brakes are applied.' },
    ],
    practice: {
      warmUp: [
        cQ('p5-4.w1', 'Doubling speed makes braking distance:', ['Same', '2× longer', '4× longer', 'Half'], '4× longer', 'Quadratic relationship', 'KE proportional to v²', 'Squared'),
      ],
      level1: [
        cQ('p5-4.1', 'Increasing reaction time will:', ['Reduce thinking distance', 'Increase thinking distance', 'No effect', 'Reduce braking distance'], 'Increase thinking distance', 'Slower reaction', 'More distance covered', 'Standard'),
      ],
      level2: [
        cQ('p5-4.2', 'Worn tyres affect:', ['Thinking distance', 'Braking distance', 'Both', 'Neither'], 'Braking distance', 'Reduced grip', 'Friction issue', 'Brakes physical effect'),
      ],
    },
  }),

  gt('gcse-p5-5', 'force-extension-rp', 'Hooke\'s Law: Force & Extension (RP4)', {
    pageRef: 'P5.5',
    intro: 'Up to the limit of proportionality, extension is directly proportional to force: F = k × e, where k = spring constant (N/m). Below limit: linear graph through origin. Beyond limit: graph curves — proportionality breaks. Energy stored in stretched spring = ½ k e² (= EPE).',
    keyPoints: [
      'F = k × e (Hooke\'s law)',
      'k = spring constant (N/m)',
      'Limit of proportionality: graph stops being straight',
      'Energy stored = ½ k e²',
      'Two springs in series: extension doubles for same F',
      'Two springs in parallel: extension halves for same F',
    ],
    rp: {
      title: 'Required Practical: Investigate the relationship between force and extension for a spring',
      aqaRef: 'AQA Required Practical (Physics) 7',
      aim: 'Determine the spring constant of a spring and identify its limit of proportionality.',
      method: [
        'Hang spring from a clamp stand. Record natural length L₀.',
        'Attach a hanger (treat as initial known weight if needed).',
        'Add 1 N (100 g) to hanger; record new length L. Calculate extension = L − L₀.',
        'Repeat in 1 N steps up to ~10 N.',
        'Plot force (y) against extension (x). Straight-line region: k = gradient.',
        'Beyond limit of proportionality: graph curves.',
      ],
      safety: 'Wear safety glasses (in case spring snaps). Use weight tray to catch falling masses.',
      tips: [
        'Take readings going UP and DOWN — average for accuracy.',
        'Check for parallax error: read scale at eye level.',
      ],
    },
    we: {
      problem: 'A spring extends 0.05 m when a force of 10 N is applied. Calculate spring constant.',
      steps: [
        { step: 1, instruction: 'F = k e → k = F/e', working: '10 ÷ 0.05' },
        { step: 2, instruction: 'Evaluate', working: '= 200 N/m' },
      ],
      answer: 'k = 200 N/m',
    },
    glossary: [
      { term: 'Hooke\'s law', definition: 'F = k e (force directly proportional to extension below limit).' },
      { term: 'Spring constant', definition: 'Force per unit extension (N/m); stiffness.' },
      { term: 'Limit of proportionality', definition: 'Point above which extension stops being proportional to force.' },
    ],
    practice: {
      warmUp: [
        nQ('p5-5.w1', 'F = 8 N, e = 0.04 m. k? (N/m)', '200', 'F/e', '8/0.04', '3 digits'),
      ],
      level1: [
        nQ('p5-5.1', 'k = 50 N/m, e = 0.2 m. F? (N)', '10', 'k×e', '50 × 0.2', '2 digits'),
        nQ('p5-5.2', 'EPE if k = 100 N/m, e = 0.1 m? (J)', '0.5', '½ k e²', '½ × 100 × 0.01', 'Decimal'),
      ],
      level2: [
        cQ('p5-5.3', 'Beyond limit of proportionality, the graph:', ['Stays straight', 'Curves (extension > expected)', 'Goes flat', 'Goes negative'], 'Curves (extension > expected)', 'Spring permanently deformed', 'Out of linear range', 'Standard'),
      ],
    },
  }),
];

// =============================================================
// P6 — Waves
// =============================================================
export const p6WavesTopics = [
  gt('gcse-p6-1', 'wave-properties', 'Wave Properties & Types', {
    pageRef: 'P6.1',
    intro: 'Waves transfer energy without transferring matter. Transverse: oscillations perpendicular to wave direction (light, EM, water surface). Longitudinal: oscillations parallel to direction (sound, P-waves). Key terms: wavelength λ (one full cycle, m), frequency f (cycles per second, Hz), period T (time for one cycle, s = 1/f), amplitude (max displacement). Wave speed: v = f × λ.',
    keyPoints: [
      'Transverse: ⊥ oscillations (light, EM)',
      'Longitudinal: ∥ oscillations (sound)',
      'v = f λ',
      'f = 1 / T',
      'Energy transferred, NOT matter',
    ],
    we: {
      problem: 'A wave has frequency 50 Hz and wavelength 6 m. Calculate the wave speed.',
      steps: [
        { step: 1, instruction: 'v = f λ', working: '50 × 6' },
        { step: 2, instruction: 'Evaluate', working: '= 300 m/s' },
      ],
      answer: '300 m/s',
    },
    glossary: [
      { term: 'Wavelength (λ)', definition: 'Distance for one complete wave cycle.' },
      { term: 'Frequency (f)', definition: 'Number of cycles per second; Hz.' },
      { term: 'Period (T)', definition: 'Time for one complete cycle; T = 1/f.' },
      { term: 'Transverse', definition: 'Wave with oscillation perpendicular to direction of energy travel.' },
      { term: 'Longitudinal', definition: 'Wave with oscillation parallel to direction of energy travel.' },
    ],
    practice: {
      warmUp: [
        cQ('p6-1.w1', 'Sound waves are:', ['Transverse', 'Longitudinal', 'Both', 'Neither'], 'Longitudinal', '∥ oscillation', 'Compressions', 'Rarefactions'),
        nQ('p6-1.w2', 'f = 5 Hz, T = ?', '0.2', 'T = 1/f', '1/5', 'Decimal'),
      ],
      level1: [
        nQ('p6-1.1', 'v = 200 m/s, λ = 4 m. f? (Hz)', '50', 'v/λ', '200/4', '2 digits'),
        nQ('p6-1.2', 'T = 0.01 s. f? (Hz)', '100', '1/T', '1/0.01', '3 digits'),
      ],
      level2: [
        nQ('p6-1.3', 'Light frequency 6 × 10¹⁴ Hz, λ in nm? (Use c = 3 × 10⁸ m/s. Answer in nm.)', '500', 'λ = c/f = 3e8 / 6e14 = 5e-7 m = 500 nm', '500 nm', 'Visible green'),
      ],
    },
  }),

  gt('gcse-p6-2', 'em-spectrum', 'The Electromagnetic Spectrum', {
    pageRef: 'P6.2',
    intro: 'EM spectrum (low f → high f, low E → high E): RADIO, MICRO, INFRARED, VISIBLE, ULTRAVIOLET, X-ray, GAMMA. All transverse waves; all travel at c = 3 × 10⁸ m/s in vacuum. Uses: radio (broadcast, MRI), micro (cooking, satellite), IR (heaters, remote, thermal imaging), visible (sight, photosynthesis), UV (suntan, sterilisation), X-ray (medical imaging), gamma (radiotherapy). Higher f = higher energy = more dangerous (UV → cancer; γ damages cells but kills tumours).',
    keyPoints: [
      'Order: Radio, Microwave, IR, Visible, UV, X-ray, Gamma (RMIVUXG mnemonic)',
      'All travel at c = 3 × 10⁸ m/s in vacuum',
      'Higher f → higher energy → more dangerous',
      'Visible: 400-700 nm (violet → red)',
      'IR cameras detect thermal radiation',
    ],
    glossary: [
      { term: 'EM spectrum', definition: 'Range of all electromagnetic waves.' },
      { term: 'Speed of light', definition: 'c = 3 × 10⁸ m/s in vacuum.' },
    ],
    practice: {
      warmUp: [
        cQ('p6-2.w1', 'Highest frequency in this list:', ['Radio', 'Visible', 'X-ray', 'Gamma'], 'Gamma', 'Top of spectrum', 'Most energetic', 'Beyond X-ray'),
      ],
      level1: [
        cQ('p6-2.1', 'Used in remote controls?', ['UV', 'IR', 'Microwaves', 'X-ray'], 'IR', 'Infrared', 'Heat radiation', 'Common use'),
      ],
      level2: [
        cQ('p6-2.2', 'Why is UV harmful to skin?', ['Wavelength too long', 'Energy high enough to damage cells', 'Always reflected', 'Cools tissue'], 'Energy high enough to damage cells', 'High-frequency EM', 'DNA damage', 'Higher than visible'),
      ],
    },
  }),

  gt('gcse-p6-3', 'reflection-refraction-rp', 'Reflection, Refraction & Lenses (RP5)', {
    pageRef: 'P6.3',
    intro: 'Reflection: angle of incidence = angle of reflection (both from normal). Refraction: light bends when crossing into a different medium because it changes speed. Toward normal entering denser medium; away from normal exiting. Lenses: convex (converging — focuses light to a point); concave (diverging — light spreads). Focal length f = distance from lens to focal point.',
    keyPoints: [
      'Reflection: i = r (from normal)',
      'Refraction: bends toward normal in denser medium',
      'Convex lens: focuses → real image on screen',
      'Concave lens: diverges → virtual image only',
      'Focal length: distance from lens to focal point',
    ],
    rp: {
      title: 'Required Practical: Investigate the reflection of light by different surfaces and refraction in different substances',
      aqaRef: 'AQA Required Practical (Physics) 8',
      aim: 'Show light bends when crossing into a different medium and measure the angles.',
      method: [
        'Place a glass block on paper. Trace its outline.',
        'Shine a ray from a ray box at an angle of incidence (e.g. 30°).',
        'Mark the entry and exit ray positions.',
        'Remove the block. Draw normal lines at entry and exit.',
        'Measure i (entry angle) and r (refracted angle inside glass).',
        'Repeat for 5 angles. Plot sin i against sin r — straight line confirms refraction.',
      ],
      safety: 'Don\'t look directly into ray box. Glass blocks have sharp edges.',
      tips: [
        'Mark rays as PAIRS of dots — easier to draw a straight line through.',
        'Use a sharp pencil at edge of block — fewer parallax issues.',
      ],
    },
    glossary: [
      { term: 'Refraction', definition: 'Change in wave direction due to change in speed when crossing media.' },
      { term: 'Convex lens', definition: 'Converging lens — focuses parallel rays to one point.' },
      { term: 'Concave lens', definition: 'Diverging lens — spreads parallel rays apart.' },
    ],
    practice: {
      warmUp: [
        cQ('p6-3.w1', 'For reflection: angle of incidence = angle of:', ['Refraction', 'Reflection', 'Normal', 'Critical'], 'Reflection', 'Standard law', 'i = r', 'Both from normal'),
      ],
      level1: [
        cQ('p6-3.1', 'Light entering glass from air:', ['Speeds up', 'Slows down', 'Stops', 'Changes colour'], 'Slows down', 'Denser medium', 'Refraction occurs', 'Speed change'),
      ],
      level2: [
        cQ('p6-3.2', 'A convex lens always:', ['Diverges light', 'Focuses parallel rays at the focal point', 'Reflects', 'Inverts colour'], 'Focuses parallel rays at the focal point', 'Converging lens', 'Real image possible', 'Standard'),
      ],
    },
  }),
];

// =============================================================
// P7 — Magnetism & Electromagnetism
// =============================================================
export const p7MagnetismTopics = [
  gt('gcse-p7-1', 'magnets-magnetic-fields', 'Magnets & Magnetic Fields', {
    pageRef: 'P7.1',
    intro: 'Magnets have N and S poles. Like poles repel, unlike attract. Magnetic field = region where another magnet/iron experiences force. Field lines go from N to S OUTSIDE the magnet (S to N inside). Field strongest where lines are closest. Iron, cobalt, nickel are ferromagnetic. Earth has its own magnetic field — that\'s why a compass works.',
    keyPoints: [
      'Like poles repel, unlike poles attract',
      'Field lines: N → S (outside)',
      'Closer lines = stronger field',
      'Ferromagnetic: Fe, Co, Ni',
      'Earth\'s magnetic field aligns compasses',
    ],
    glossary: [
      { term: 'Magnetic field', definition: 'Region around a magnet where another magnet or magnetic material experiences a force.' },
      { term: 'Ferromagnetic', definition: 'Material that can be permanently magnetised (Fe, Co, Ni).' },
    ],
    practice: {
      warmUp: [
        cQ('p7-1.w1', 'Like poles:', ['Attract', 'Repel', 'No interaction', 'Pull apart fast'], 'Repel', 'Standard rule', 'Same poles push apart', 'Magnetism rule'),
      ],
      level1: [
        cQ('p7-1.1', 'Field lines OUTSIDE a magnet go:', ['S to N', 'N to S', 'In all directions', 'In circles'], 'N to S', 'Convention', 'Outside', 'Inside they go S to N'),
      ],
      level2: [
        tQ('p7-1.2', 'Material that can be permanently magnetised: ___magnetic.', 'ferro', '5 letters', 'F____', 'Iron-based'),
      ],
    },
  }),

  gt('gcse-p7-2', 'electromagnets', 'Current-Carrying Wires & Electromagnets', {
    pageRef: 'P7.2',
    intro: 'A current in a wire creates a magnetic field around it. Right-hand grip rule: thumb points along current direction → fingers curl in direction of field. A coil/solenoid creates a stronger field, and adding an iron core gives an electromagnet. Strength controllable by: current, number of turns, iron core. Uses: electric bells, scrap-yard cranes, MRI scanners, motors.',
    keyPoints: [
      'Current → magnetic field around wire',
      'Right-hand grip rule for direction',
      'Solenoid: coil produces stronger field along axis',
      'Electromagnet: solenoid + iron core',
      'Stronger by: ↑ current, ↑ turns, iron core',
      'Uses: bells, cranes, motors',
    ],
    glossary: [
      { term: 'Solenoid', definition: 'Coil of wire that produces a magnetic field when current flows.' },
      { term: 'Electromagnet', definition: 'Magnet made by passing current through a solenoid wrapped around an iron core.' },
    ],
    practice: {
      warmUp: [
        cQ('p7-2.w1', 'A wire with no current has:', ['Strong B-field', 'No magnetic field around it', 'Always positive', 'Field everywhere'], 'No magnetic field around it', 'Need current', 'Field comes from moving charge', 'Standard'),
      ],
      level1: [
        cQ('p7-2.1', 'To increase electromagnet strength:', ['Lower current', 'Use copper core', 'Increase number of turns', 'Cool the wire'], 'Increase number of turns', 'More loops', 'Stronger field', 'Standard'),
      ],
      level2: [
        tQ('p7-2.2', 'A coil + iron core makes an ___.', 'electromagnet', '13 letters', 'E____________', 'Switchable magnet'),
      ],
    },
  }),

  gt('gcse-p7-3', 'motor-effect-fleming', 'The Motor Effect & Fleming\'s Left-Hand Rule', {
    pageRef: 'P7.3',
    tier: 'higher',
    intro: 'Current-carrying wire in magnetic field experiences a FORCE — the motor effect. F = B × I × L (force, magnetic flux density T, current A, length m of wire in field). Direction by Fleming\'s LEFT-hand rule: First finger = Field (B), seCond finger = Current (I), thuMb = Motion (force). Motors use this principle: a current-carrying coil in a B-field rotates because forces on opposite sides act in opposite directions.',
    keyPoints: [
      'F = B I L',
      'Fleming\'s LEFT hand: F-Mo-C → F=Field, ToM=Motion, sCond=Current',
      'B in tesla (T), I in A, L in m, F in N',
      'Motor: coil in field rotates because forces on opposite sides oppose',
      'Reverse direction: reverse current OR reverse B',
    ],
    we: {
      problem: 'A 0.2 m wire carrying 5 A is in a 0.3 T magnetic field perpendicular to current. Calculate the force.',
      steps: [
        { step: 1, instruction: 'F = B I L', working: '0.3 × 5 × 0.2' },
        { step: 2, instruction: 'Evaluate', working: '= 0.3 N' },
      ],
      answer: 'F = 0.3 N',
    },
    glossary: [
      { term: 'Motor effect', definition: 'A current-carrying wire in a magnetic field experiences a force.' },
      { term: 'Magnetic flux density', definition: 'Strength of magnetic field; tesla (T).' },
    ],
    practice: {
      warmUp: [
        nQ('p7-3.w1', 'B = 0.5 T, I = 4 A, L = 0.1 m. F? (N)', '0.2', 'BIL', '0.5 × 4 × 0.1', 'Decimal'),
      ],
      level1: [
        cQ('p7-3.1', 'Reversing current direction reverses:', ['Field strength', 'Force direction', 'Wire length', 'Magnetic flux density'], 'Force direction', 'Fleming swaps', 'Standard rule', 'Direction flip'),
      ],
      level2: [
        nQ('p7-3.2', 'F = 0.6 N, I = 2 A, L = 0.5 m. B (T)?', '0.6', 'B = F/(IL)', '0.6/(2×0.5)', 'Decimal'),
      ],
    },
  }),

  gt('gcse-p7-4', 'transformers', 'Transformers (Higher)', {
    pageRef: 'P7.4',
    tier: 'higher',
    intro: 'Transformers change a.c. voltage. Primary coil (input) and secondary coil (output) wrapped on a soft iron core. AC in primary creates changing B-field in core → induces alternating EMF in secondary. Transformer equation: Vp/Vs = Np/Ns. For a perfectly efficient transformer: Vp × Ip = Vs × Is. Step-up: more secondary turns = higher V (used to send power across pylons at 400 kV — much smaller current → less I²R loss). Step-down: fewer secondary turns = lower V (for safe domestic use).',
    keyPoints: [
      'Vp/Vs = Np/Ns',
      'For ideal transformer: Vp Ip = Vs Is',
      'Step-up: more secondary turns → higher V, smaller I',
      'Step-down: fewer secondary turns → lower V',
      'National grid: step-up for transmission → step-down for homes',
    ],
    we: {
      problem: 'Primary 200 turns at 230 V; secondary 50 turns. Find Vs.',
      steps: [
        { step: 1, instruction: 'Vp/Vs = Np/Ns → Vs = Vp × Ns/Np', working: '230 × 50/200' },
        { step: 2, instruction: 'Evaluate', working: '= 57.5 V' },
      ],
      answer: 'Vs = 57.5 V (step-down)',
    },
    glossary: [
      { term: 'Transformer', definition: 'Device that changes the voltage of an a.c. supply.' },
    ],
    practice: {
      warmUp: [
        nQ('p7-4.w1', 'Np = 100, Ns = 1000, Vp = 230 V. Vs (V)?', '2300', '230 × 1000/100', 'Step-up', '4 digits'),
      ],
      level1: [
        cQ('p7-4.1', 'In National Grid, step-up transformer is used to:', ['Lower voltage', 'Raise V to reduce I²R losses', 'Convert AC to DC', 'Cool the wires'], 'Raise V to reduce I²R losses', 'Power transmission', 'Smaller I = less heat', 'Reasoning'),
      ],
      level2: [
        nQ('p7-4.2', 'Vp × Ip = Vs × Is. Vp=230, Ip=4. Vs=460 then Is = ? A', '2', 'Power conserved', '230×4 = 460×Is', '1 digit'),
      ],
    },
  }),
];

// =============================================================
// Section bundles
// =============================================================
export const allPhysicsGCSEBundles = {
  p1: p1EnergyTopics,
  p2: p2ElectricityTopics,
  p3: p3ParticleModelTopics,
  p4: p4AtomicStructureTopics,
  p5: p5ForcesTopics,
  p6: p6WavesTopics,
  p7: p7MagnetismTopics,
};
